import { BLOCK_TYPE, type BlockType } from '@/types/block';

interface ToolbarPolicyContext {
  parentStableId: string | null;
  parentBlockType: BlockType | null;
  lockedSection?: boolean;
}

interface ParentToolbarPolicyContext extends ToolbarPolicyContext {
  isGroupBlock: boolean;
}

interface LeafToolbarPolicyContext extends ToolbarPolicyContext {
  blockType: BlockType;
}

export interface ToolbarPolicy {
  canSelectParent: boolean;
  canShowGroupSettings: boolean;
}

export interface LeafToolbarPolicy extends ToolbarPolicy {
  canPrimaryEdit: boolean;
  canDelete: boolean;
  showsToolbarOnHover: boolean;
}

function getLeafPolicyOverrides(
  blockType: BlockType,
): Partial<Pick<LeafToolbarPolicy, 'canPrimaryEdit' | 'canDelete' | 'showsToolbarOnHover'>> {
  switch (blockType) {
    case BLOCK_TYPE.REVIEW_INDEX:
      return {
        canPrimaryEdit: false,
        canDelete: false,
        showsToolbarOnHover: true,
      };
    default:
      return {};
  }
}

export function resolveLeafToolbarPolicy({
  parentStableId,
  parentBlockType,
  blockType,
  lockedSection = false,
}: LeafToolbarPolicyContext): LeafToolbarPolicy {
  if (lockedSection) {
    return {
      canSelectParent: false,
      canShowGroupSettings: false,
      canPrimaryEdit: true,
      canDelete: false,
      showsToolbarOnHover: false,
    };
  }

  return {
    canSelectParent: Boolean(parentStableId) && parentBlockType === 'group',
    canShowGroupSettings: false,
    canPrimaryEdit: true,
    canDelete: true,
    showsToolbarOnHover: false,
    ...getLeafPolicyOverrides(blockType),
  };
}

export function resolveParentToolbarPolicy({
  parentStableId,
  parentBlockType,
  isGroupBlock,
  lockedSection = false,
}: ParentToolbarPolicyContext): ToolbarPolicy {
  if (lockedSection) {
    return { canSelectParent: false, canShowGroupSettings: false };
  }

  const canSelectGroupParent = Boolean(parentStableId) && parentBlockType === 'group';

  return {
    canSelectParent: canSelectGroupParent,
    canShowGroupSettings: isGroupBlock,
  };
}
